export type LegalDocumentId = 'terms' | 'disclaimer' | 'safety' | 'privacy';

export interface LegalDocument {
  id: LegalDocumentId;
  title: string;
  lastUpdated: string;
  sections: { heading: string; paragraphs: string[] }[];
}

const termsZh: LegalDocument = {
  id: 'terms',
  title: '用户服务协议',
  lastUpdated: '最后更新：2026 年 8 月',
  sections: [
    {
      heading: '平台性质',
      paragraphs: [
        'MassUniHub 仅为麻省高校学生提供闲置物品的信息展示与交流平台。平台不拥有、不代售、不检验任何展示物品，亦不参与买卖双方的实际交易与资金流转。',
        '平台不设资金池、不做资金托管、不对交易抽取佣金，也不提供任何形式的搬运或配送服务。',
      ],
    },
    {
      heading: '交易独立性',
      paragraphs: [
        '物品买卖合同与权利义务关系严格仅存在于买方与卖方之间。平台仅提供信息展示、校园身份校验与站内联络工具。',
      ],
    },
    {
      heading: '质量与资金豁免',
      paragraphs: [
        '平台不对任何物品的真实性、安全性、质量及合法性作明示或暗示的担保。交易款项均由买卖双方线下自行结算（例如 Zelle、微信或现金）。因物品瑕疵、虚假交易或资金交付引发的任何民事争议，由买卖双方自行协商解决，本平台不承担任何连带法律责任。',
      ],
    },
    {
      heading: '账户与学生身份认证',
      paragraphs: [
        '注册需使用学校 .edu 邮箱。🎓 .edu 已认证标签仅表示用户持有可用的学校邮箱，不构成信用背书、身份保证或交易担保。',
        '平台有权封禁虚假、欺诈或违规发布账号，无需事先通知。',
      ],
    },
    {
      heading: '用户义务',
      paragraphs: [
        '卖家须保证商品来源合法，并如实披露瑕疵；买家须自行核验物品质量。双方应在当面验货后再完成付款与交付。',
      ],
    },
    {
      heading: '条款变更',
      paragraphs: [
        '平台可更新本协议。继续使用即表示同意更新后的条款。',
      ],
    },
  ],
};

const termsEn: LegalDocument = {
  id: 'terms',
  title: 'Terms of Service',
  lastUpdated: 'Last updated: August 2026',
  sections: [
    {
      heading: 'Nature of the platform',
      paragraphs: [
        'MassUniHub is an information and matching platform for Massachusetts college students to list unused items. The platform does not own, consign, inspect, or take possession of any listed item, and it does not participate in the actual sale or transfer of funds.',
        'MassUniHub does not operate an escrow or funds pool, does not take a commission on trades, and does not provide moving or delivery services.',
      ],
    },
    {
      heading: 'Independence of the transaction',
      paragraphs: [
        'Any contract and all rights and obligations exist solely between buyer and seller. The platform only provides listings, student-email checks, and in-app contact tools.',
      ],
    },
    {
      heading: 'No quality or payment guarantee',
      paragraphs: [
        'The platform makes no express or implied warranty as to authenticity, safety, quality, or legality of any item. Payment is settled directly between the parties (for example Zelle, WeChat, or cash). Disputes arising from defects, fraud, or payment are to be resolved by the parties; MassUniHub has no joint or several liability.',
      ],
    },
    {
      heading: 'Accounts and student verification',
      paragraphs: [
        'Registration requires a school .edu email. The 🎓 .edu verified badge only means the user holds a working school mailbox. It is not a credit endorsement, identity guarantee, or transaction warranty.',
        'The platform may suspend or ban accounts for false, fraudulent, or policy-violating listings without prior notice.',
      ],
    },
    {
      heading: 'User obligations',
      paragraphs: [
        'Sellers must list items from a lawful source and disclose defects honestly. Buyers must inspect quality themselves. Meet in person, inspect first, then pay and take the item.',
      ],
    },
    {
      heading: 'Changes to terms',
      paragraphs: [
        'These terms may be updated. Continued use constitutes acceptance of the updated terms.',
      ],
    },
  ],
};

const disclaimerZh: LegalDocument = {
  id: 'disclaimer',
  title: '免责声明',
  lastUpdated: '最后更新：2026 年 8 月',
  sections: [
    {
      heading: '平台',
      paragraphs: [
        'MassUniHub 验证 .edu 邮箱或学生身份真实性，并可封禁虚假、欺诈或违规发布账号。',
        '平台完全免责：不参与交易双方的资金结算，不对物品质量承担任何保证，不对交易履约纠纷承担连带赔偿责任。',
      ],
    },
    {
      heading: '卖家',
      paragraphs: [
        '卖家可自行设定商品标价与交易方式；在全额收到货款前有权拒绝交付物品。',
        '卖家须保证商品来源合法，并如实披露瑕疵；承担商品质量瑕疵担保责任；线下当面完成交接，钱货两清。',
      ],
    },
    {
      heading: '买家',
      paragraphs: [
        '买家享有现场当面验货权；发现实物与描述严重不符时有权拒绝购买。',
        '买家须自行核验物品质量（如电器运作、家具结构）；通过 Zelle / 微信 / 现金直接向卖家结算货款；物品自提离场后自行承担后续使用损耗。',
      ],
    },
    {
      heading: '搬运与运输',
      paragraphs: [
        '本平台仅提供闲置信息发布。如涉及大件家具搬运，建议买卖双方私信沟通自提方式，或通过外部渠道寻找有车校友协助。',
        '平台不安排司机、不承运货物，不对货损、空跑或人身伤害承担责任。',
      ],
    },
  ],
};

const disclaimerEn: LegalDocument = {
  id: 'disclaimer',
  title: 'Disclaimer',
  lastUpdated: 'Last updated: August 2026',
  sections: [
    {
      heading: 'Platform',
      paragraphs: [
        'MassUniHub verifies .edu email or student identity and may ban false, fraudulent, or violating accounts.',
        'The platform is fully disclaimed: it does not settle funds, does not warrant item quality, and has no joint liability for performance disputes.',
      ],
    },
    {
      heading: 'Seller',
      paragraphs: [
        'Sellers set their own price and trade method, and may refuse to hand over an item until full payment is received.',
        'Sellers must ensure a lawful source, disclose defects honestly, stand behind quality as between the parties, and complete an in-person, cash-on-delivery style handover.',
      ],
    },
    {
      heading: 'Buyer',
      paragraphs: [
        'Buyers may inspect in person and may refuse to buy if the item is materially different from the listing.',
        'Buyers must check quality themselves (for example whether an appliance works or furniture is structurally sound), pay the seller directly via Zelle / WeChat / cash, and accept subsequent wear after pickup.',
      ],
    },
    {
      heading: 'Moving and transport',
      paragraphs: [
        'This platform only publishes unused-item listings. For large furniture, buyers and sellers should message each other to arrange pickup, or find a classmate with a car through other channels.',
        'The platform does not dispatch drivers or carry goods, and is not liable for cargo damage, wasted trips, or personal injury.',
      ],
    },
  ],
};

const safetyZh: LegalDocument = {
  id: 'safety',
  title: '安全交易与防骗提示',
  lastUpdated: '最后更新：2026 年 8 月',
  sections: [
    {
      heading: '见面与验货',
      paragraphs: [
        '请在白天、公共场合见面（例如校园大厅、图书馆门口），尽量结伴。',
        '电器请通电试机；家具请检查结构、污渍与缺失零件。先验货，后付款。',
      ],
    },
    {
      heading: '付款',
      paragraphs: [
        '不要支付定金、不要代付运费、不要扫码「退款」。平台不经手货款。',
        '推荐当面使用 Zelle、微信或现金结算。卖家未确认收款前不要交货。',
      ],
    },
    {
      heading: '识别可疑账号',
      paragraphs: [
        '警惕拒绝当面交易、价格过低并催促付款、盗用图片、或要求站外私下转账的账号。',
        '如遇可疑情况，请停止交易并在站内联系对方后保存记录；平台可封禁违规账号。',
      ],
    },
  ],
};

const safetyEn: LegalDocument = {
  id: 'safety',
  title: 'Safety tips',
  lastUpdated: 'Last updated: August 2026',
  sections: [
    {
      heading: 'Meeting and inspecting',
      paragraphs: [
        'Meet in daylight in a public place (campus lobby, library entrance). Bring a friend when you can.',
        'Plug in appliances to test them. Check furniture structure, stains, and missing parts. Inspect first, then pay.',
      ],
    },
    {
      heading: 'Paying',
      paragraphs: [
        'Do not pay deposits, shipping on behalf of a stranger, or scan QR codes for “refunds.” The platform never handles payment.',
        'Prefer in-person Zelle, WeChat, or cash. Sellers should not hand over the item until payment is confirmed.',
      ],
    },
    {
      heading: 'Spotting suspicious accounts',
      paragraphs: [
        'Be wary of people who refuse to meet, push a too-good-to-be-true price, reuse stolen photos, or insist on off-platform payment.',
        'Stop the trade if something feels wrong, keep your in-app messages, and report the account. The platform may ban violating users.',
      ],
    },
  ],
};

const privacyZh: LegalDocument = {
  id: 'privacy',
  title: '隐私政策',
  lastUpdated: '最后更新：2026 年 10 月',
  sections: [
    {
      heading: '我们收集哪些信息',
      paragraphs: [
        '账户信息：学校 .edu 邮箱、姓名、密码（仅以加密哈希形式保存）、学校名称；你可选填的手机号和微信号。',
        '你发布的内容：商品标题、描述、价格、地标、取货说明和商品照片。照片上传时会重新编码，去除拍摄位置等元数据。',
        '站内私信内容、你收藏的商品，以及你提交的举报及其原因。',
        '验证和安全记录：邮箱验证码与密码重置链接只以哈希形式短期保存；发送次数用于防止滥用。',
      ],
    },
    {
      heading: '为什么收集',
      paragraphs: [
        '用 .edu 邮箱确认你是在校学生，展示“已认证”标签；让买卖双方联系、完成线下交易；处理举报、封禁违规账户、防止诈骗。',
        '我们不出售你的个人信息，不用于广告。',
      ],
    },
    {
      heading: '谁能看到你的信息',
      paragraphs: [
        '任何访问者（包括未登录用户）都能看到你发布的商品，以及卖家的姓名、学校和认证状态。',
        '卖家的微信号（如果你填写了）只对已登录并完成 .edu 邮箱验证的同学显示，并且每个账户的查看次数有限制，防止被批量收集。不想提供微信号，请在个人资料中留空，买家仍可通过站内私信联系你。',
        '你的邮箱和手机号不会展示给其他用户。私信只有对话双方可见；平台管理员在处理举报时可以查看相关账户和内容。',
        '为提供服务，我们使用以下服务商处理数据：Railway（服务器与数据库托管）、Cloudflare R2（商品图片存储）、Resend（发送验证邮件）。',
      ],
    },
    {
      heading: '保存多久',
      paragraphs: [
        '账户存续期间，我们保存你的账户信息、商品和私信。你删除商品后，商品不再公开展示，但记录会保留用于处理纠纷和举报。',
        '验证码 10 分钟后失效，密码重置链接到期后失效。',
      ],
    },
    {
      heading: '删除账户和数据',
      paragraphs: [
        '目前请发送邮件至 support@massunihub.com 申请删除账户，邮件请从你注册使用的 .edu 邮箱发出。我们会在 30 天内删除你的账户信息、商品和照片；涉及未处理举报的记录可能在处理完成后再删除。',
      ],
    },
    {
      heading: '联系我们',
      paragraphs: ['对本政策有任何疑问，请联系 support@massunihub.com。'],
    },
  ],
};

const privacyEn: LegalDocument = {
  id: 'privacy',
  title: 'Privacy Policy',
  lastUpdated: 'Last updated: October 2026',
  sections: [
    {
      heading: 'What we collect',
      paragraphs: [
        'Account details: your school .edu email, name, password (stored only as a salted hash), and school; optionally your phone number and WeChat ID.',
        'What you post: listing title, description, price, neighborhood, pickup details, and photos. Photos are re-encoded on upload, which strips metadata such as location.',
        'In-app messages, saved listings, and any reports you file with their reasons.',
        'Verification and security records: email codes and password-reset links are kept only as short-lived hashes; send counts are kept to prevent abuse.',
      ],
    },
    {
      heading: 'Why we collect it',
      paragraphs: [
        'To confirm you are a current student via your .edu email and show the verified badge; to let buyers and sellers contact each other and trade in person; to handle reports, ban abusive accounts, and prevent scams.',
        'We do not sell your personal information or use it for advertising.',
      ],
    },
    {
      heading: 'Who can see it',
      paragraphs: [
        'Anyone, including visitors who are not signed in, can see your listings and the seller’s name, school, and verification status.',
        'A seller’s WeChat ID (if you added one) is shown only to signed-in students with a verified .edu email, and each account can view a limited number per hour to prevent bulk collection. Leave WeChat ID blank in your profile if you prefer; buyers can still reach you through in-app messages.',
        'Your email and phone number are not shown to other users. Messages are visible only to the two participants; administrators can review the accounts and content involved in a report.',
        'We use these providers to run the service: Railway (server and database hosting), Cloudflare R2 (listing photo storage), and Resend (verification email).',
      ],
    },
    {
      heading: 'How long we keep it',
      paragraphs: [
        'We keep your account details, listings, and messages while your account exists. Deleted listings are no longer shown publicly but their records are kept to resolve disputes and reports.',
        'Verification codes expire after 10 minutes; password-reset links expire when their window ends.',
      ],
    },
    {
      heading: 'Deleting your account and data',
      paragraphs: [
        'For now, email support@massunihub.com from the .edu address you registered with to request deletion. We delete your account details, listings, and photos within 30 days; records tied to an open report may be deleted once the report is resolved.',
      ],
    },
    {
      heading: 'Contact',
      paragraphs: ['Questions about this policy: support@massunihub.com.'],
    },
  ],
};

const DOCUMENTS: Record<LegalDocumentId, { zh: LegalDocument; en: LegalDocument }> = {
  terms: { zh: termsZh, en: termsEn },
  disclaimer: { zh: disclaimerZh, en: disclaimerEn },
  safety: { zh: safetyZh, en: safetyEn },
  privacy: { zh: privacyZh, en: privacyEn },
};

export function getLegalDocument(id: LegalDocumentId, language: string): LegalDocument {
  const pair = DOCUMENTS[id];
  return language.startsWith('zh') ? pair.zh : pair.en;
}
